<div class="packaging-preview">
  <div class="toolbar">
    <mat-checkbox
      [(ngModel)]="differencesOnly"
      [disabled]="differenceCount === 0"
      (ngModelChange)="onFilterChange()"
    >
      Show only packages that differ
    </mat-checkbox>
    <mat-checkbox
      [ngModel]="showAllRules"
      (ngModelChange)="setAllRulesExpanded($event)"
      [disabled]="visiblePackages.length === 0"
    >
      Show all rules
    </mat-checkbox>
    @if (showMethodologyToggle) {
      <mat-checkbox [(ngModel)]="showMethodologyNeed">
        {{ methodologyToggleLabel }}
      </mat-checkbox>
    }
    @if (differenceCount > 0) {
      <span class="diff-summary">{{ differenceCount }} package{{ differenceCount === 1 ? "" : "s" }} differ</span>
    }
  </div>

  @if (sections.length === 0) {
    <mat-card appearance="outlined" class="empty-state">
      <mat-card-content>
        @if (packages.length === 0) {
          <p>No packaging steps recorded for this session.</p>
        } @else {
          <p>No packaging differences. Uncheck “Show only packages that differ” to see the full ledger.</p>
        }
      </mat-card-content>
    </mat-card>
  } @else {
    @for (section of sections; track section.id) {
      <section class="ledger-section" [class.ledger-section--contribution]="section.id === 'contribution'">
        <h2 class="section-title">{{ section.title }}</h2>
        @if (section.note) {
          <p class="section-note">{{ section.note }}</p>
        }

        <div class="compare-row">
          @for (side of sides; track side.key) {
            <mat-card class="ledger-card" appearance="outlined">
              <mat-card-header>
                <h3>{{ side.label }}</h3>
              </mat-card-header>
              <mat-card-content>
                @let columns = columnsFor(section.id);
                <div
                  class="ledger"
                  [class.ledger--contribution]="section.id === 'contribution'"
                  [class.ledger--aid]="section.id === 'aid'"
                  [class.ledger--methodology]="section.id === 'aid' && showMethodologyNeed"
                  role="table"
                >
                  <div class="ledger-head" role="row">
                    <span class="col col-package" role="columnheader">Package</span>
                    @for (col of columns; track col.id) {
                      <span class="col num" [class.col-total]="col.kind === 'total'" role="columnheader">{{ col.label }}</span>
                    }
                  </div>

                  @if (sectionStarting(section.id, side.key); as starting) {
                    <div class="ledger-row ledger-row--start" role="row">
                      <span class="col col-package" role="rowheader">Starting totals</span>
                      @for (col of columns; track col.id) {
                        @if (col.kind === 'amount') {
                          <span class="col num" role="cell">—</span>
                        } @else {
                          <span class="col num" [class.col-total]="col.kind === 'total'" role="cell">
                            {{ startingValue(starting, col.id) | number }}
                          </span>
                        }
                      }
                    </div>
                  }

                  @for (pkg of section.packages; track pkg.packId) {
                    @if (stepFor(pkg, side.key); as step) {
                      <div class="ledger-block" [class.changed]="pkg.hasDifference" [class.expanded]="pkg.expanded">
                        <div class="ledger-row" role="row">
                          <span class="col col-package" role="rowheader">
                            <button
                              type="button"
                              class="package-toggle"
                              (click)="toggleExpanded(pkg)"
                              [attr.aria-expanded]="pkg.expanded"
                              [attr.aria-label]="(pkg.expanded ? 'Hide' : 'Show') + ' rules for ' + pkg.displayName"
                            >
                              <mat-icon [fontIcon]="pkg.expanded ? 'expand_less' : 'chevron_right'" aria-hidden="true"></mat-icon>
                              <span class="package-name">{{ pkg.displayName }}</span>
                            </button>
                          </span>
                          @for (col of columns; track col.id) {
                            @if (col.kind === 'amount') {
                              <span class="col num col-amount" role="cell">{{ step.amount | number }}</span>
                            } @else {
                              @if (metricChanged(step, columnMetricKey(col.id)); as changed) {
                                <span
                                  class="col num col-with-delta"
                                  [class.col-total]="col.kind === 'total'"
                                  [class.col-changed]="changed"
                                  role="cell"
                                  [attr.title]="'Changed by ' + formatDelta(metricDelta(step, columnMetricKey(col.id)))"
                                >
                                  <span class="delta">{{ formatDelta(metricDelta(step, columnMetricKey(col.id))) }}</span>
                                  {{ col.value(step) | number }}
                                </span>
                              } @else {
                                <span
                                  class="col num"
                                  [class.col-total]="col.kind === 'total'"
                                  role="cell"
                                >
                                  {{ col.value(step) | number }}
                                </span>
                              }
                            }
                          }
                        </div>

                        @if (pkg.expanded) {
                          <div class="rules">
                            @if (step.rules.length > 0) {
                              @for (rule of step.rules; track $index) {
                                <div class="rule">
                                  <span class="rule-population">{{ rule.population }}</span>
                                  <span class="rule-meta">
                                    {{ rule.category || "—" }} · {{ rule.assignType || "—" }} · {{ ruleAmountLabel(rule) }}
                                    @if (rule.input) {
                                      ·
                                      @if (ruleInputUsesPipe(rule)) {
                                        {{ rule.input | packageSelectorName }}
                                      } @else {
                                        {{ rule.input }}
                                      }
                                    }
                                  </span>
                                </div>
                              }
                            } @else {
                              <p class="rule-empty">No rules applied.</p>
                            }
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="ledger-row ledger-row--missing" role="row">
                        <span class="col col-package" role="rowheader">{{ pkg.displayName }}</span>
                        <span class="col col-span muted" role="cell">Not in {{ side.label }} run</span>
                      </div>
                    }
                  }
                </div>
              </mat-card-content>
            </mat-card>
          }
        </div>
      </section>
    }
  }
</div>
